import React, { useState } from 'react';
import { Mail, Lock, Eye, EyeOff, Loader2, KeyRound, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { verifyOTP, resendOTP, requestPasswordReset, resendVerification } from '../services/api';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  
  // MFA state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  
  // View state
  const [view, setView] = useState('login'); // 'login', 'mfa', 'forgot', 'verify-email'

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.mfaRequired) {
      setMfaRequired(true);
      setView('mfa');
      setSuccess('Verification code sent to your email');
    } else if (result.emailNotVerified) {
      setView('verify-email');
      setError('Please verify your email before logging in.');
    } else if (!result.success) {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleOTPSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await verifyOTP(email, otp);
      // Store token and user data
      localStorage.setItem('token', result.access_token);
      localStorage.setItem('user', JSON.stringify(result.user));
      window.location.reload();
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid verification code');
    }
    
    setLoading(false);
  };

  const handleResendOTP = async () => {
    setOtpSending(true);
    try {
      await resendOTP(email);
      setSuccess('A new verification code has been sent');
    } catch {
      setError('Failed to resend code');
    }
    setOtpSending(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await requestPasswordReset(email);
      setSuccess('If an account exists, a password reset link has been sent to your email.');
    } catch {
      setSuccess('If an account exists, a password reset link has been sent to your email.');
    }
    
    setLoading(false);
  };

  const handleResendVerification = async () => {
    setLoading(true);
    try {
      await resendVerification(email);
      setSuccess('Verification email sent. Please check your inbox.');
    } catch {
      setError('Failed to send verification email');
    }
    setLoading(false);
  };

  const renderMFAView = () => (
    <div className="bg-white rounded-2xl shadow-2xl p-8">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-[#393d8a]/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <KeyRound className="w-8 h-8 text-[#393d8a]" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Verification Required</h2>
        <p className="text-gray-500 mt-1">Enter the code sent to {email}</p>
      </div>

      <form onSubmit={handleOTPSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="otp">Verification Code</Label>
          <Input
            id="otp"
            type="text"
            placeholder="Enter 6-digit code"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="h-12 text-center text-2xl tracking-widest"
            maxLength={6}
            required
          />
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
            {error}
          </div>
        )}
        
        {success && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm">
            {success}
          </div>
        )}

        <Button
          type="submit"
          className="w-full h-12 bg-[#393d8a] hover:bg-[#393d8a]/90 text-white font-semibold"
          disabled={loading || otp.length !== 6}
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Verifying...
            </>
          ) : (
            'Verify Code'
          )}
        </Button>
        
        <div className="flex justify-between items-center text-sm">
          <button
            type="button"
            onClick={() => { setView('login'); setMfaRequired(false); setOtp(''); setError(''); }}
            className="text-gray-500 hover:text-gray-700 flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" /> Back to login
          </button>
          <button
            type="button"
            onClick={handleResendOTP}
            disabled={otpSending}
            className="text-[#393d8a] hover:underline"
          >
            {otpSending ? 'Sending...' : 'Resend code'}
          </button>
        </div>
      </form>
    </div>
  );

  const renderForgotPasswordView = () => (
    <div className="bg-white rounded-2xl shadow-2xl p-8">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Reset Password</h2>
        <p className="text-gray-500 mt-1">Enter your email to receive a reset link</p>
      </div>

      <form onSubmit={handleForgotPassword} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="reset-email">Email Address</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              id="reset-email"
              type="email"
              placeholder="you@postaruba.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-11 h-12"
              required
            />
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
            {error}
          </div>
        )}
        
        {success && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm">
            {success}
          </div>
        )}

        <Button
          type="submit"
          className="w-full h-12 bg-[#393d8a] hover:bg-[#393d8a]/90 text-white font-semibold"
          disabled={loading}
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Sending...
            </>
          ) : (
            'Send Reset Link'
          )}
        </Button>
        
        <button
          type="button"
          onClick={() => { setView('login'); setError(''); setSuccess(''); }}
          className="w-full text-center text-gray-500 hover:text-gray-700 text-sm flex items-center justify-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" /> Back to login
        </button>
      </form>
    </div>
  );

  const renderVerifyEmailView = () => (
    <div className="bg-white rounded-2xl shadow-2xl p-8">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Mail className="w-8 h-8 text-yellow-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Verify Your Email</h2>
        <p className="text-gray-500 mt-1">Please check your inbox and click the verification link</p>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm mb-4">
          {error}
        </div>
      )}
      
      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-600 text-sm mb-4">
          {success}
        </div>
      )}

      <div className="space-y-4">
        <Button
          onClick={handleResendVerification}
          className="w-full h-12 bg-[#eeb224] hover:bg-[#eeb224]/90 text-white font-semibold"
          disabled={loading}
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Sending...
            </>
          ) : (
            'Resend Verification Email'
          )}
        </Button>
        
        <button
          type="button"
          onClick={() => { setView('login'); setError(''); setSuccess(''); }}
          className="w-full text-center text-gray-500 hover:text-gray-700 text-sm flex items-center justify-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" /> Back to login
        </button>
      </div>
    </div>
  );

  const renderLoginView = () => (
    <div className="bg-white rounded-2xl shadow-2xl p-8">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Welcome Back</h2>
        <p className="text-gray-500 mt-1">Sign in to your account</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="email">Email Address</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              id="email"
              type="email"
              placeholder="you@postaruba.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-11 h-12"
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <Label htmlFor="password">Password</Label>
            <button
              type="button"
              onClick={() => { setView('forgot'); setError(''); setSuccess(''); }}
              className="text-xs text-[#393d8a] hover:underline"
            >
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-11 pr-11 h-12"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
            {error}
          </div>
        )}

        <Button
          type="submit"
          className="w-full h-12 bg-[#393d8a] hover:bg-[#393d8a]/90 text-white font-semibold"
          disabled={loading}
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Signing in...
            </>
          ) : (
            'Sign In'
          )}
        </Button>
      </form>

      <div className="mt-6 pt-6 border-t border-gray-100">
        <p className="text-center text-sm text-gray-500">
          Having trouble signing in? Contact your administrator.
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#393d8a] via-[#393d8a]/90 to-[#30a34b]/80 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_helpdesk-hub-41/artifacts/0bvfy7jq_1_Post_Aruba_Logo.png" 
            alt="Post Aruba Logo" 
            className="w-32 h-32 mx-auto mb-4 object-contain"
          />
          <h1 className="text-3xl font-bold text-white">Post Aruba</h1>
          <p className="text-white/80 mt-1">Support Center</p>
        </div>

        {/* Dynamic View */}
        {view === 'mfa' && renderMFAView()}
        {view === 'forgot' && renderForgotPasswordView()}
        {view === 'verify-email' && renderVerifyEmailView()}
        {view === 'login' && renderLoginView()}

        <p className="text-center text-white/60 text-sm mt-6">
          © {new Date().getFullYear()} Post Aruba. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
